import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// SVG Shapes for wave backgrounds
const TopWaveSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
    <path fill="#e6f8fa" d="M0,160L48,149.3C96,139,192,117,288,128C384,139,480,181,576,176C672,171,768,117,864,101.3C960,85,1056,107,1152,117.3C1248,128,1344,128,1392,128L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
  </svg>
);

const MidWaveSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
    <path fill="#e6f8fa" d="M0,160L48,144C96,128,192,96,288,85.3C384,75,480,85,576,112C672,139,768,181,864,197.3C960,213,1056,203,1152,170.7C1248,139,1344,85,1392,58.7L1440,32L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
  </svg>
);

const BottomWaveSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
    <path fill="#0099ff" d="M0,192L48,181.3C96,171,192,149,288,160C384,171,480,213,576,208C672,203,768,149,864,122.7C960,96,1056,96,1152,112C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
  </svg>
);

// Gradient Button Component
const GradientButton = styled.button`
  background: linear-gradient(to right, #00c6ff, #0072ff);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 198, 255, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 198, 255, 0.4);
  }
`;

// Main Styled Components
const PageContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  color: #333;
  overflow-x: hidden;
  background-color: white;
`;

const NavBar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 80px;
  position: relative;
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #0099ff;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 40px;
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  font-size: 16px;
  transition: color 0.3s ease;
  
  &:hover {
    color: #0099ff;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 15px;
`;

const LoginButton = styled.button`
  background: transparent;
  border: 2px solid #0099ff;
  color: #0099ff;
  border-radius: 30px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 153, 255, 0.1);
  }
`;

const SignupButton = styled(GradientButton)`
  padding: 10px 20px;
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  padding: 50px 80px 100px;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 550px;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 30px;
  max-width: 450px;
`;

const HeroImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  position: relative;
`;

const HeroImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const WaveTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 300px;
  z-index: -1;
  overflow: hidden;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const WaveBottom = styled.div`
  position: absolute;
  bottom: -100px;
  left: 0;
  width: 100%;
  height: 300px;
  z-index: -1;
  transform: rotate(180deg);
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const SectionSubtitle = styled.div`
  font-size: 18px;
  margin-bottom: 40px;
  color: #666;
  
  span.highlight {
    color: #0099ff;
    font-weight: 600;
  }
`;

const AboutSection = styled.section`
  padding: 80px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background-color: #fff;
`;

const AboutImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
`;

const AboutImage = styled.img`
  max-width: 90%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const AboutContent = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const AboutLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0099ff;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const AboutSectionWave = styled.div`
  position: absolute;
  bottom: -100px;
  left: 0;
  width: 100%;
  height: 300px;
  z-index: -1;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const TeamSection = styled.section`
  padding: 80px;
  position: relative;
  overflow: hidden;
  background-color: #fff;
  text-align: center;
`;

const TeamLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0099ff;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin: 40px auto;
  max-width: 800px;
`;

const TeamMember = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const TeamMemberImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const FeaturedTeamSection = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 60px;
  position: relative;
  z-index: 1;
`;

const FeaturedTeamMember = styled.div`
  flex: 1;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
`;

const FeaturedInfo = styled.div`
  padding: 20px;
  text-align: left;
`;

const FeaturedName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const FeaturedRole = styled.p`
  color: #0099ff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
`;

const FeaturedBio = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #666;
`;

const NavigationArrows = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 2;
  pointer-events: none;
`;

const NavArrow = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  
  &:hover {
    background: #f8f8f8;
  }
`;

const TeamSectionWave = styled.div`
  position: absolute;
  bottom: -100px;
  left: 0;
  width: 100%;
  height: 300px;
  z-index: -1;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ServicesSection = styled.section`
  padding: 120px 80px 200px;
  position: relative;
  background: linear-gradient(180deg, #0099ff 0%, #00d4ff 100%);
  text-align: right;
  color: white;
  overflow: hidden;
`;

const ServiceWaveTop = styled.div`
  position: absolute;
  top: -1px;
  left: 0;
  width: 100%;
  height: 150px;
  z-index: 1;
  
  svg {
    width: 100%;
    height: 100%;
    transform: rotate(180deg);
  }
`;

const ServiceHeading = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  text-align: right;
  padding-right: 5%;
`;

const ServiceTitle = styled.h2`
  color: white;
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const ServiceCards = styled.div`
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 50px auto 0;
  position: relative;
  z-index: 2;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 25px;
  flex: 1;
  text-align: center;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
`;

const ServiceCardTitle = styled.h3`
  color: #333;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 15px;
`;

const ServiceCardText = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
`;

const PortfolioSection = styled.section`
  padding: 80px;
  position: relative;
  overflow: hidden;
  background-color: #fff;
  text-align: center;
`;

const PortfolioTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 50px;
  color: #333;
  
  span {
    color: #0099ff;
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PortfolioItem = styled.div`
  border-radius: 15px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const PortfolioArrows = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
`;

const PortfolioArrow = styled(NavArrow)`
  background: #f0f0f0;
  color: #333;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const ContactSection = styled.section`
  padding: 80px;
  position: relative;
  background: linear-gradient(to right, #0099ff, #00d4ff);
  display: flex;
  align-items: center;
  color: white;
`;

const ContactContent = styled.div`
  flex: 1;
  max-width: 500px;
`;

const ContactTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
`;

const ContactText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const ContactButton = styled.button`
  background: white;
  color: #0099ff;
  border: none;
  border-radius: 30px;
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f5f5f5;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ContactForm = styled.div`
  flex: 1;
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  max-width: 500px;
`;

const FormTitle = styled.h3`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  margin-bottom: 15px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const SubmitButton = styled(GradientButton)`
  width: 100%;
`;

const Footer = styled.footer`
  background: #0099ff;
  color: white;
  padding: 15px 0;
  text-align: center;
  font-size: 14px;
`;

const CustomSoftwarePage = () => {
  const navigate = useNavigate();

  // Sample image URLs - replace with your actual images
  const heroImageUrl = "https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80";
  const aboutImageUrl = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80";
  
  // Team images
  const teamMembers = [
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1565884280295-98eb83e74f96?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1548142813-c348350df52b?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1573497019236-61e1a29614a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80",
  ];
  
  // Portfolio images
  const portfolioItems = [
    "https://images.unsplash.com/photo-1606857521015-7f9fcf423740?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80",
  ];

  return (
    <PageContainer>
      <WaveTop>
        <TopWaveSVG />
      </WaveTop>
      
      <NavBar>
        <Logo>BBGlobal</Logo>
        <NavLinks>
          <NavLink href="#">About</NavLink>
          <NavLink href="#">Team</NavLink>
          <NavLink href="#">Service</NavLink>
          <NavLink href="#">Work</NavLink>
          <NavLink href="#">Contact</NavLink>
        </NavLinks>
        <NavButtons>
          <LoginButton>Sign In</LoginButton>
          <SignupButton>Sign Up</SignupButton>
        </NavButtons>
      </NavBar>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>Create Your Own Stunning Custom Software</HeroTitle>
          <HeroSubtitle>
            Unleash your business potential with custom software solutions designed to streamline operations, enhance productivity, and drive growth in the digital landscape.
          </HeroSubtitle>
          <GradientButton onClick={() => navigate('/business-consultation')}>
            Get Started
          </GradientButton>
        </HeroContent>
        <HeroImageContainer>
          <HeroImage src={heroImageUrl} alt="Custom software development" />
        </HeroImageContainer>
        <WaveBottom>
          <MidWaveSVG />
        </WaveBottom>
      </HeroSection>

      <AboutSection>
        <AboutImageContainer>
          <AboutImage src={aboutImageUrl} alt="Team working on software" />
        </AboutImageContainer>
        <AboutContent>
          <AboutLabel>About Us</AboutLabel>
          <SectionTitle>We Create <span style={{ color: '#0099ff' }}>Pretty</span> & <span style={{ color: '#0099ff' }}>Modern</span> Software</SectionTitle>
          <SectionSubtitle>
            B&B Global Solutions provides elegant, functional custom software that combines 
            modern design with powerful features. Our solutions enhance business processes, 
            boost productivity, and deliver exceptional user experiences.
          </SectionSubtitle>
          <GradientButton onClick={() => navigate('/business-consultation')}>
            Create Your Software
          </GradientButton>
        </AboutContent>
        <AboutSectionWave>
          <MidWaveSVG />
        </AboutSectionWave>
      </AboutSection>

      <TeamSection>
        <TeamLabel>Our Team</TeamLabel>
        <SectionTitle>Create Your Software with <span style={{ color: '#0099ff' }}>Our Best Team Ever</span></SectionTitle>
        
        <TeamGrid>
          {teamMembers.map((src, index) => (
            <TeamMember key={index}>
              <TeamMemberImage src={src} alt={`Team member ${index + 1}`} />
            </TeamMember>
          ))}
        </TeamGrid>

        <FeaturedTeamSection>
          <NavigationArrows>
            <NavArrow>←</NavArrow>
            <NavArrow>→</NavArrow>
          </NavigationArrows>
          
          <FeaturedTeamMember>
            <FeaturedImage src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80" alt="Jenita Yonna" />
            <FeaturedInfo>
              <FeaturedRole>Web Developer</FeaturedRole>
              <FeaturedName>Jenita Yonna</FeaturedName>
              <FeaturedBio>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Magna odio pellentesque adipiscing eget. Donec at aliquet ullamcorper aliquam. Dolor adipiscing ac lorem pulvinar.
              </FeaturedBio>
            </FeaturedInfo>
          </FeaturedTeamMember>
          
          <FeaturedTeamMember>
            <FeaturedImage src="https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80" alt="Mrs. Beauty" />
            <FeaturedInfo>
              <FeaturedRole>Frontend Developer</FeaturedRole>
              <FeaturedName>Michael Roberts</FeaturedName>
              <FeaturedBio>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Magna odio pellentesque adipiscing eget. Donec at aliquet ullamcorper aliquam. Dolor adipiscing ac lorem pulvinar.
              </FeaturedBio>
            </FeaturedInfo>
          </FeaturedTeamMember>
        </FeaturedTeamSection>
        
        <TeamSectionWave>
          <MidWaveSVG />
        </TeamSectionWave>
      </TeamSection>

      <ServicesSection>
        <ServiceWaveTop>
          <BottomWaveSVG />
        </ServiceWaveTop>
        
        <ServiceHeading>
          <TeamLabel style={{ color: 'white' }}>Our Service</TeamLabel>
          <ServiceTitle>"Customer is King"</ServiceTitle>
        </ServiceHeading>
        
        <ServiceCards>
          <ServiceCard>
            <ServiceCardTitle>Fast</ServiceCardTitle>
            <ServiceCardText>
              We optimize our website for better performance, ensuring fast loading times.
            </ServiceCardText>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceCardTitle>Secure</ServiceCardTitle>
            <ServiceCardText>
              Our website security is the most secure, protecting your data.
            </ServiceCardText>
          </ServiceCard>
          
          <ServiceCard>
            <ServiceCardTitle>Beautiful</ServiceCardTitle>
            <ServiceCardText>
              Our design prioritizes the UX/UI of the website, making it visually appealing.
            </ServiceCardText>
          </ServiceCard>
        </ServiceCards>
      </ServicesSection>

      <PortfolioSection>
        <PortfolioTitle>Our Latest <span>Work</span></PortfolioTitle>
        
        <PortfolioGrid>
          {portfolioItems.map((src, index) => (
            <PortfolioItem key={index}>
              <PortfolioImage src={src} alt={`Portfolio item ${index + 1}`} />
            </PortfolioItem>
          ))}
        </PortfolioGrid>
        
        <PortfolioArrows>
          <PortfolioArrow>←</PortfolioArrow>
          <PortfolioArrow>→</PortfolioArrow>
        </PortfolioArrows>
      </PortfolioSection>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Create Software for Your Business</ContactTitle>
          <ContactText>
            Lorem ipsum dolor sit amet consectetur adipiscing elit tempuibos esadip eusmod ipsum elit congue luctus ipsum congue.
          </ContactText>
          <ContactButton>Create Website Now</ContactButton>
        </ContactContent>
        
        <ContactForm>
          <FormTitle>Let's have friendly chat about how we can help you.</FormTitle>
          
          <FormRow>
            <FormInput placeholder="Your Name" />
          </FormRow>
          
          <FormRow>
            <FormInput placeholder="Your Email" />
          </FormRow>
          
          <FormRow>
            <FormInput placeholder="I'm Designer" />
          </FormRow>
          
          <FormRow>
            <FormTextarea placeholder="Write message..." />
          </FormRow>
          
          <SubmitButton>Send Message</SubmitButton>
        </ContactForm>
      </ContactSection>
      
      <Footer>
        WebDev © 2023 | Privacy Policy | All Rights Reserved
      </Footer>
    </PageContainer>
  );
};

export default CustomSoftwarePage; 